<template>
  <v-card>
    <v-layout>
      <!-- Phone: the drawer is an overlay opened from this bar -->
      <v-app-bar
        v-if="showNav && isPhone"
        :color="modeColor"
        density="compact"
        flat
        class="app-bar"
      >
        <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
        <img
          :src="require('@/assets/logo_innovent.png')"
          alt="Innovent"
          class="app-bar__logo"
        />
      </v-app-bar>

      <!--
        Desktop: permanent rail that expands on hover.
        Phone: temporary overlay (no rail, no hover: touch devices emulate
        mouseenter/mouseleave on tap, which collapsed the groups right after
        opening them).
      -->
      <v-navigation-drawer
        v-if="showNav"
        v-model="drawer"
        :color="modeColor"
        class="app-nav"
        :class="{ 'app-nav--phone': isPhone }"
        :expand-on-hover="!isPhone"
        :rail="!isPhone"
        :permanent="!isPhone"
        :temporary="isPhone"
        @mouseleave="onDrawerLeave"
      >
        <!-- ============ Logo ============ -->
        <div class="app-nav__brand">
          <img
            :src="require('@/assets/logo_innovent.png')"
            alt="Innovent"
            class="app-nav__logo"
          />
        </div>
        <v-divider class="app-nav__divider"></v-divider>

        <v-list v-model:opened="openedGroups" density="compact" nav>
          <v-list-item
          density="compact"
            active-class="secondary"
            :to="homeItem.path == '#' ? '' : homeItem.path"
            link
            :prepend-icon="homeItem.icon"
            :title="$filters.capitalize($t(homeItem.title, 1))"
          >
          </v-list-item>

          <!-- ============ Production section ============ -->
          <v-list-group value="production" no-action>
            <template v-slot:activator="{ props }">
              <v-list-item
              density="compact"
                v-bind="props"
                :title="$filters.capitalize($t('menu.production'))"
                prepend-icon="mdi-lightning-bolt-circle"
              ></v-list-item>
            </template>

            <v-list-item
              v-for="item in visibleProductionItems"
              active-class="secondary"
              :key="item.title"
              :to="item.external ? undefined : (item.path == '#' ? '' : item.path)"
              :href="item.external ? item.path : undefined"
              :target="item.external ? '_blank' : undefined"
              :prepend-icon="item.icon"
              :append-icon="item.external ? 'mdi-open-in-new' : undefined"
              :title="$filters.capitalize($t(item.title))"
              density="compact"
              link
            >
            </v-list-item>
          </v-list-group>

          <!-- ============ Main section ============ -->
          <v-list-item
            v-for="item in items"
            active-class="secondary"
            :key="item.title"
            :to="item.path == '#' ? '' : item.path"
            :prepend-icon="item.icon"
            :title="
              item.multiple
                ? $filters.capitalize($t(item.title, 10))
                : $filters.capitalize($t(item.title, 1))
            "
            density="compact"
            link
          >
          </v-list-item>

          <!-- ============ Admin section ============ -->
          <v-list-group
              v-if="$store.getters.isUserFromStaff"
              value="admin"
            >
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-cog"
                :title="$filters.capitalize($t('menu.admin'))"
                density="compact"
              >
              </v-list-item>
            </template>

            <v-list-item
              v-for="item in adminItems"
              active-class="secondary"
              :key="item.title"
              :to="item.path == '#' ? '' : item.path"
              :prepend-icon="item.icon"
              :title="$filters.capitalize($t(item.title))"
              density="compact"
              link
            >
            </v-list-item>
          </v-list-group>
        </v-list>

        <!-- ============ Admin section : buttons ============ -->
        <template v-slot:append>
          <v-divider class="app-nav__divider"></v-divider>
          <v-list density="compact" nav>
            <v-list-item
              active-class="secondary"
              :to="{ name: 'help' }"
              prepend-icon="mdi-help-circle-outline"
              :title="$filters.capitalize($t('help.label', 1))"
              density="compact"
              link
            >
            </v-list-item>
            <v-list-item
              v-for="item in endItems"
              active-class="secondary"
              :key="item.title"
              :to="item.path == '#' ? '' : item.path"
              :prepend-icon="item.icon"
              :title="$filters.capitalize($t(item.title))"
              density="compact"
              link
            >
            </v-list-item>
          </v-list>
        </template>
      </v-navigation-drawer>

      <v-main>
        <!-- fill-height doesn't work because https://stackoverflow.com/a/65578074. set the height on v-main as 100vh cause no appbar so no spill -->
        <v-container fluid class="pa-0" style="min-height: 100vh">
          <router-view id="router_view"></router-view>
        </v-container>
      </v-main>
      <Snackbar></Snackbar>
    </v-layout>
  </v-card>
</template>

<script>
import Snackbar from "@/components/Snackbar";
import { isSgeUser } from "@/services/sge";

export default {

  name: "Fujin",
  components: { Snackbar },

  computed: {
    isPhone() {
      return this.$vuetify.display.smAndDown;
    },
    isSolarProfil() {
      return this.$store.getters.isSolarProfil;
    },
    visibleProductionItems() {
      const sgeUser = isSgeUser(this.$store.state.user);
      return this.productionItems.filter((item) => !item.sgeOnly || sgeUser);
    },
    activeGroups() {
      // Groups holding the current route: kept open so the menu shows
      // where the user is, and restored when the mouse leaves the drawer.
      const path = this.$route.path;
      const inGroup = (items) =>
        items.some((item) => !item.external && item.path === path);
      const groups = [];
      if (inGroup(this.productionItems)) groups.push("production");
      if (inGroup(this.adminItems)) groups.push("admin");
      return groups;
    },
    modeColor() {
      switch (process.env.NODE_ENV) {
        case "production":
          return "primary";
        default:
          return "pink";
      }
    },
  },

  watch: {
    isPhone: {
      immediate: true,
      handler(phone) {
        // overlay closed by default on phone, always shown on desktop
        this.drawer = !phone;
      },
    },
    activeGroups: {
      immediate: true,
      handler(groups) {
        this.openedGroups = groups;
      },
    },
    $route(to) {
      document.title = to.meta.title || "Fujin";
      if (typeof this.$route.meta.showNav === "boolean") {
        this.showNav = to.meta.showNav;
      } else {
        this.showNav = true;
      }
    },
  },

  created() {
    this.setBase();
  },
  beforeUpdate() {
    this.setBase();
  },

  methods: {
    onDrawerLeave() {
      // Desktop only: fold the groups back to the active one when the
      // hover-expanded rail collapses. On phone there is no hover.
      if (!this.isPhone) this.openedGroups = this.activeGroups;
    },
    navMod() {
      this.drawer = !this.isPhone;
      this.showNav = false;
    },
    setBase() {
    if (typeof this.$route.meta.showNav === "boolean") {
      this.showNav = this.$route.meta.showNav;
    } else this.navMod();
    if (!this.$store.getters.isLoggedIn) return
    if (this.$store.state.producers?.all.length <= 0 &&
      !this.$store.state.producers.isLoading) {
      this.$store.dispatch('producers/getAll');
    };
    if (this.$store.state.stockages?.all.length <= 0 &&
      !this.$store.state.stockages.isLoading) {
      this.$store.dispatch('stockages/getAll');
    };
    },
  },

  data() {
    return {
      drawer: true,
      showNav: true,
      openedGroups: [],
      homeItem: { title: "menu.home", icon: "mdi-home", path: "/" },
      items: [
        {
          title: "menu.scheduler",
          icon: "mdi-chart-timeline",
          path: "/scheduler",
        },
        {
          title: "part_view.label",
          icon: "mdi-barcode",
          path: "/part_view",
          multiple: true,
        },
        {
          title: "part_edition.menu",
          icon: "mdi-barcode-scan",
          path: "/part_edition",
        },
        {
          title: "intervention_reports.label",
          icon: "mdi-clipboard-file-outline",
          path: "/intervention_reports",
          multiple: true,
        },
        {
          title: "todos.label",
          icon: "mdi-clipboard-check-multiple",
          path: "/todo_view",
        },
        {
          title: "purchase_view.label",
          icon: "mdi-shopping",
          path: "/purchase_view",
          multiple: true,
        },
        {
          title: "maintenances.label",
          icon: "mdi-wrench",
          path: "/maintenance_view",
          multiple: true,
        },
      ],
      productionItems: [
        {
          title: "menu.solar_production",
          icon: "mdi-solar-panel",
          path: "/solar_production",
        },
        {
          title: "menu.wind_production",
          icon: "mdi-wind-turbine",
          path: "/wind_production",
        },
        {
          title: "menu.batteries",
          icon: "mdi-battery-charging-high",
          path: "/batteries",
        },
        {
          title: "menu.automaton",
          icon: "mdi-boom-gate-outline",
          path: "http://134.122.57.21:3000/d/bd8255eb-2868-4918-a6c6-b2c84e40c312/pdl?orgId=1&from=now-6h&to=now&timezone=browser&var-instance=croixrault&refresh=10s",
          external: true,
        },
        {
          title: "prod_availability.label",
          icon: "mdi-eye-outline",
          path: "/prod_availability",
        },
        {
          title: "prod_availability_months.label",
          icon: "mdi-eye-settings-outline",
          path: "/prod_availability_month",
        },
        { title: "menu.telemetry", icon: "mdi-chart-bar", path: "/telemetry" },
        // {
        //   title: "analytics.label",
        //   icon: "mdi-home-analytics",
        //   path: "/analytics",
        // },
        {
          title: "limitations.label",
          icon: "mdi-tortoise",
          path: "/limitations",
        },
        {
          title: "alarms.label",
          icon: "mdi-alert-decagram",
          path: "/alarm_view",
        },
        {
          title: "menu.sge",
          icon: "mdi-transmission-tower",
          path: "/sge",
          sgeOnly: true,
        },
        {
          title: "menu.enedis_losses",
          icon: "mdi-lightning-bolt",
          path: "https://enedis-losses.innovent.fr/",
          external: true,
        },
        {
          title: "menu.enedis_injection",
          icon: "mdi-transmission-tower-import",
          path: "/enedis_injection",
        },
      ],
      adminItems: [
        { title: "stockages.label", icon: "mdi-city", path: "/stockages" },
        { title: "places.label", icon: "mdi-bookshelf", path: "/places" },
        {
          title: "power_stations.label",
          icon: "mdi-city-variant",
          path: "/power_stations",
        },
        {
          title: "producer_types.label",
          icon: "mdi-speaker-multiple",
          path: "/producer_types",
        },
        {
          title: "producers.label",
          icon: "mdi-guitar-electric",
          path: "/producers",
        },
        {
          title: "part_categories.label",
          icon: "mdi-cat",
          path: "/part_categories",
        },
        {
          title: "suppliers.label",
          icon: "mdi-package-variant-closed",
          path: "/suppliers",
        },
        {
          title: "stock_parts.label",
          icon: "mdi-tooltip-edit",
          path: "/stock_parts",
        },
        { title: "units.label", icon: "mdi-barcode", path: "/units" },
        {
          title: "consumables.label",
          icon: "mdi-barcode-off",
          path: "/consumables",
        },
        {
          title: "external_repairs.label",
          icon: "mdi-wrench",
          path: "/external_repairs",
        },
        {
          title: "internal_repairs.label",
          icon: "mdi-wrench",
          path: "/internal_repairs",
        },
        {
          title: "todos.label",
          icon: "mdi-clipboard-check-multiple",
          path: "/todos",
        },
        {
          title: "affectations.label",
          icon: "mdi-link-variant",
          path: "/affectations",
        },
        {
          title: "purchases.label",
          icon: "mdi-cart-outline",
          path: "/purchases",
        },
        {
          title: "purchase_lines.label",
          icon: "mdi-cart-plus",
          path: "/purchase_lines",
        },
        {
          title: "preventive_maintenances.label",
          icon: "mdi-doctor",
          path: "/preventive_maintenances",
        },
        {
          title: "verif_machines.label",
          icon: "mdi-wrench",
          path: "/verif_machine",
        },
        {
          title: "alarm_codes.label",
          icon: "mdi-alarm-light",
          path: "/alarm_codes",
        },
        {
          title: "spot_price_contracts.label",
          icon: "mdi-draw-pen",
          path: "/spot_price_contracts",
        },
        {
          title: "menu.PRSP",
          icon: "mdi-alarm-light",
          path: "/performance_ratio_solar_panel",
        },
      ],
      endItems: [
        { title: "menu.user", icon: "mdi-account-circle", path: "/user" },
        { title: "menu.logout", icon: "mdi-logout", path: "/logout" },
      ],
      background: false,
    };
  },
};
</script>

<style lang="scss">

.v-main {
  background-color: #f7f5f0;

  // The router-view id lands on each view's root element (usually its
  // v-container): one small, consistent gutter around every view.
  #router_view {
    padding: 8px;
  }

  // White blocks (toolbars, tables) read as cards on the beige background.
  .v-toolbar,
  .v-table {
    border-radius: 8px;
    overflow: hidden;
  }
  .v-table {
    box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
      0 1px 3px 0 rgba(0, 0, 0, 0.12);
  }
  .v-toolbar + .v-table {
    margin-top: 8px;
  }
}

th {
  text-transform: capitalize;
}

td {
text-align: left;
}

// ---------- Navigation drawer ----------
.app-nav {
  // The drawer scrolls when the menu is taller than the window, but the
  // scrollbar itself stays hidden (it also showed up while expanding).
  .v-navigation-drawer__content {
    overflow-x: hidden;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__brand {
    padding: 8px;
  }
  // Logo on the drawer background, rendered in white like the menu text.
  // Rail: the left part of the image (the emblem).
  // Expanded (hover): the whole logo.
  &__logo {
    display: block;
    box-sizing: border-box;
    height: 40px;
    width: 40px;
    padding: 4px;
    object-fit: cover;
    object-position: left center;
    filter: brightness(0) invert(1);
    transition: width 0.2s ease;
  }
  &.v-navigation-drawer--is-hovering &__logo,
  &--phone &__logo {
    width: 100%;
    object-fit: contain;
    object-position: center;
  }
  &__divider {
    margin: 0 12px;
    opacity: 0.35;
  }

  .v-list-group__items .v-list-item {
    // nested entries stay aligned with the rail icons
    padding-inline-start: 8px !important;
  }
  .v-list-item--active {
    font-weight: 600;
  }
}

// ---------- Phone app bar ----------
.app-bar {
  &__logo {
    height: 32px;
    padding: 2px 8px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
}

.v-btn {
  border-radius: 4px !important;
}

// ---------- Date picker (VueDatePicker) aligned with the outlined fields ----------
.dp__input {
  height: 40px;
  border-radius: 4px;
  border-color: rgba(0, 0, 0, 0.38);
  font-family: inherit;
  font-size: 0.875rem;
}
.dp__input:hover {
  border-color: rgba(0, 0, 0, 0.87);
}
.dp__input_focus,
.dp__input:focus {
  border-color: rgb(var(--v-theme-primary));
}
.dp__theme_light {
  --dp-primary-color: #0093bf;
}
</style>
